import React, { useEffect, useState, useRef } from "react";
import { buildCloudinaryImageUrl } from "../../../utils/CloudinaryImageUrl";
/* ══════════════════════════════════════════
   ICONS
══════════════════════════════════════════ */
const IcoUp = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);
const IcoDown = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

/* ══════════════════════════════════════════
   SKELETON
══════════════════════════════════════════ */
const Skeleton = ({ visible }) => (
  <div className={`hc-skeleton${visible ? "" : " hide"}`}>
    <div className="hc-sk-bg" />
    <div className="hc-sk-ui">
      <div className="hc-sk-block hc-sk-tag" />
      <div className="hc-sk-block hc-sk-h1" />
      <div className="hc-sk-block hc-sk-h2" />
      <div className="hc-sk-block hc-sk-sub" />
    </div>
    <div className="hc-sk-dots">
      <div className="hc-sk-dot" /><div className="hc-sk-dot" /><div className="hc-sk-dot" />
    </div>
    <div className="hc-sk-count" />
  </div>
);

/* ══════════════════════════════════════════
   INNER CAROUSEL — lógica original intacta
══════════════════════════════════════════ */
// Misma URL optimizada para la precarga y para el <img> visible (una sola descarga por slide)
const slideImageUrl = (slide) =>
  buildCloudinaryImageUrl({ url: slide?.image, width: 1200, quality: "auto", type: "image" });

const CarruselImg = ({
  slides = [],
  autoplay = true,
  interval = 1000,
  transitionMs = 100
}) => {
  const [current, setCurrent]                 = useState(0);
  const [paused, setPaused]                   = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loaded, setLoaded]                   = useState(false);
  const [animKey, setAnimKey]                 = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const firstUrl = slides.length > 0 ? slideImageUrl(slides[0]) : "";
    if (!firstUrl) { setLoaded(true); return; }
    const img   = new Image();
    img.onload  = () => setLoaded(true);
    img.onerror = () => setLoaded(true);
    img.src     = firstUrl;
    return () => { img.onload = null; img.onerror = null; };
  }, [slides]);

  // Ref estable para nextSlide — el interval siempre llama la versión más reciente
  const nextSlideRef = useRef(null);

  const nextSlide = () => {
    if (isTransitioning || slides.length === 0) return;
    setIsTransitioning(true);
    setAnimKey(k => k + 1);
    setCurrent(prev => (prev + 1 >= slides.length ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), transitionMs);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setAnimKey(k => k + 1);
    setCurrent(prev => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), transitionMs);
  };

  // Siempre actualiza el ref con la función más reciente (sin deps)
  useEffect(() => { nextSlideRef.current = nextSlide; });

  // El interval NO depende de `current` — solo se recrea si cambia
  // autoplay, paused o interval. Elimina la pausa de 1 segundo entre slides.
  useEffect(() => {
    if (!autoplay || paused || slides.length <= 1) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      nextSlideRef.current?.();
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [autoplay, paused, interval, slides.length]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  if (slides.length === 0) return null;

  const displaySlides = slides;
  const activeSlide   = displaySlides[current];

  return (
    <>
      <div
        className="hc-stage"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <Skeleton visible={!loaded} />

        {/* Slides */}
        {displaySlides.map((slide, idx) => (
          <div key={idx} className={`hc-slide${idx === current ? " active" : ""}`}>
            <img src={slideImageUrl(slide)} alt={slide.title || `Slide ${idx + 1}`}
              className="hc-img" draggable="false" loading={idx === 0 ? "eager" : "lazy"} />
            <div className="hc-ov hc-ov-base" />
            <div className="hc-ov-side" />
          </div>
        ))}

        {/* ── Top bar ── */}
        <div className="hc-topbar" key={`top-${animKey}`}>
          {/* <div className="hc-brand-tag">
            <span className="hc-brand-dot" />
            <span className="hc-brand-label">Don Lee</span>
          </div> */}
          {displaySlides.length > 1 && (
            <div className="hc-counter">
              <span className="hc-counter-cur">
                {String(current + 1).padStart(2, "0")}
              </span>
              <span className="hc-counter-sep">/</span>
              <span className="hc-counter-tot">
                {String(displaySlides.length).padStart(2, "0")}
              </span>
            </div>
          )}
        </div>

        {/* ── Contenido inferior ── */}
        {loaded && (
          <div className="hc-content " key={`content-${animKey}`}>
            <div className="hc-content-left d-none">
              {/* Eyebrow */}
              <div className="hc-eyebrow-wrap hc-anim-eyebrow">
                <span className="hc-eyebrow-line hc-anim-line" />
                <span className="hc-eyebrow-text">Destacado</span>
              </div>

              {/* Título */}
              {activeSlide?.title && (
                <div className="hc-title hc-anim-title">
                  <span className="hc-title-inner">{activeSlide.title}</span>
                </div>
              )}

              {/* Subtítulo */}
              {activeSlide?.subtitle && (
                <p className="hc-subtitle hc-anim-subtitle">{activeSlide.subtitle}</p>
              )}
            </div>

            {/* Derecha: indicadores + nav */}
            {displaySlides.length > 1 && (
              <div className="hc-content-right hc-anim-right">
                {/* Indicadores verticales */}
                <div className="hc-inds">
                  {displaySlides.map((_, index) => (
                    <button key={index} className="hc-ind-btn"
                      aria-label={`Ir al slide ${index + 1}`}
                      onClick={() => {
                        if (!isTransitioning) {
                          setIsTransitioning(true);
                          setAnimKey(k => k + 1);
                          setCurrent(index);
                          setTimeout(() => setIsTransitioning(false), transitionMs);
                        }
                      }}>
                      <div className={`hc-ind-track${index === current ? " active" : " inactive"}`}>
                        {index === current && !paused && (
                          <div className="hc-ind-fill"
                            style={{ animationDuration: `${interval}ms` }} />
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Nav vertical */}
                <div className="hc-nav">
                  <button className="hc-btn" onClick={prevSlide}
                    disabled={isTransitioning} aria-label="Anterior">
                    <IcoUp />
                  </button>
                  <button className="hc-btn" onClick={nextSlide}
                    disabled={isTransitioning} aria-label="Siguiente">
                    <IcoDown />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Barra de progreso — base del stage */}
        {displaySlides.length > 1 && !paused && (
          <div className="hc-progress-bar" key={`prog-${animKey}`}>
            <div className="hc-progress-fill"
              style={{ animationDuration: `${interval}ms` }} />
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      {displaySlides.length > 1 && (
        <div className="hc-footer d-none">
          <div className="hc-footer-left">
            <span className="hc-footer-tag">En pantalla</span>
            <span className="hc-footer-sep" />
            {activeSlide?.title && (
              <span className="hc-footer-title">{activeSlide.title}</span>
            )}
          </div>
          <div className="hc-footer-count">
            <span className="hc-footer-count-bar">
              <span className="hc-footer-count-fill"
                key={`fc-${animKey}`}
                style={{ animationDuration: `${interval}ms` }} />
            </span>
            {String(current + 1).padStart(2, "0")} — {String(displaySlides.length).padStart(2, "0")}
          </div>
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════
   EXPORT — wrapper original intacto
══════════════════════════════════════════ */
export default function HeroCarruselImg({ slides = [] }) {
  return (
    <div className="hc-root"> 
      <div className="hc-outer">
        <CarruselImg
          slides={slides}
          autoplay={true}
          interval={5000}
          transitionMs={800}
        />
      </div>
    </div>
  );
}